<template>
    <div v-if="show" class="modal-overlay">
        <div class="modal-container">
            <div class="modal-header">
                <h3>Fermer la caisse</h3>
                <button class="modal-close" @click="closeModal">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                </button>
            </div>
            
            <div class="modal-body">
                <!-- Résumé des ventes -->
                <div class="summary-section">
                    <h4>Résumé des ventes</h4>
                    <div class="summary-grid">
                        <div class="summary-item">
                            <span class="summary-label">Solde initial</span>
                            <span class="summary-value">{{ formatCurrency(cashRegister.start_balance) }}</span>
                        </div>
                        <div class="summary-item">
                            <span class="summary-label">Total des ventes</span>
                            <span class="summary-value highlight">{{ formatCurrency(statistics?.ca?.ttc || 0) }}</span>
                        </div>
                        <div class="summary-item">
                            <span class="summary-label">Ventes en espèces</span>
                            <span class="summary-value">{{ formatCurrency(statistics?.by_type?.single?.ca_ttc || 0) }}</span>
                        </div>
                        <div class="summary-item danger">
                            <span class="summary-label">Remboursements/Annulations</span>
                            <span class="summary-value">{{ formatCurrency(statistics?.refunds?.amount_ttc    || 0) }}</span>
                        </div>
                        <div class="summary-item success">
                            <i></i>
                            <span class="summary-label">Paiements/Réglements</span>
                            <span class="summary-value">{{ formatCurrency(statistics?.receipts?.amount_ttc    || 0) }}</span>
                        </div>
                    </div>
                </div>

                <!-- Détails par type de vente -->
                <div class="details-section">
                    <h4>Détails par type</h4>
                    <div class="details-grid">
                        <div class="detail-card">
                            <div class="detail-icon">
                                <i class="fa-solid fa-money-bill-wave"></i>
                            </div>
                            <div class="detail-content">
                                <span class="detail-label">Ventes comptant</span>
                                <span class="detail-value">{{ formatCurrency(statistics?.by_type?.single?.ca_ttc || 0) }}</span>
                                <span class="detail-count">{{ statistics?.by_type?.single?.count || 0 }} ventes</span>
                            </div>
                        </div>
                        <div class="detail-card">
                            <div class="detail-icon">
                                <i class="fa-solid fa-clock"></i>
                            </div>
                            <div class="detail-content">
                                <span class="detail-label">Ventes différées</span>
                                <span class="detail-value">{{ formatCurrency(statistics?.by_type?.deferred?.ca_ttc || 0) }}</span>
                                <span class="detail-count">{{ statistics?.by_type?.deferred?.count || 0 }} ventes</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Solde attendu -->
                <div class="expected-balance-section">
                    <div class="expected-balance-card">
                        <span class="expected-label">Solde attendu en caisse</span>
                        <span class="expected-value">{{ formatCurrency(expectedBalance) }}</span>
                        <span class="expected-note">= Solde initial + Ventes en espèces</span>
                    </div>
                </div>

                <!-- Saisie du solde final -->
                <form @submit.prevent="submitForm">
                    <div class="form-group">
                        <label for="endBalance">Montant en caisse (espèces) *</label>
                        <div class="input-group">
                            <div class="input-prefix">FCFA</div>
                            <input 
                                id="endBalance" 
                                v-model.number="form.end_balance" 
                                type="number" 
                                min="0" 
                                step="1"
                                placeholder="0" 
                                class="form-control"
                                :class="{ 'input-error': validationErrors.end_balance }" 
                                required 
                            />
                        </div>
                        <span v-if="validationErrors.end_balance" class="field-error">
                            {{ validationErrors.end_balance[0] }}
                        </span>
                    </div>

                    <!-- Différence si montant saisi -->
                    <div v-if="form.end_balance > 0" class="difference-section">
                        <div class="difference-card" :class="differenceClass">
                            <span class="difference-label">Différence</span>
                            <span class="difference-value">{{ formatCurrency(Math.abs(difference)) }}</span>
                            <span class="difference-note">{{ differenceNote }}</span>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button type="button" class="btn-cancel" @click="closeModal">Annuler</button>
                        <button type="submit" class="btn-primary" :disabled="loading">
                            <span v-if="loading" class="spinner-small"></span>
                            Fermer la caisse
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent, ref, computed } from 'vue'
import CashRegisterService from '@/services/cashRegisterService'
import { useToastStore } from '@/store/modules/toastStore'

export default defineComponent({
    name: 'CloseCashRegisterModal',
    props: {
        show: {
            type: Boolean,
            required: true
        },
        cashRegister: {
            type: Object,
            required: true
        },
        statistics: {
            type: Object,
            default: () => ({})
        }
    },
    emits: ['close', 'success'],
    setup(props, { emit }) {
        const toastStore = useToastStore()
        const loading = ref(false)
        const form = ref({
            end_balance: 0
        })
        const validationErrors = ref({})

        const expectedBalance = computed(() => {
            const startBalance = props.cashRegister?.start_balance || 0
            const cashSales = props.statistics?.by_type?.single?.ca_ttc || 0
            return (startBalance + cashSales + props.statistics?.refunds?.amount_ttc + props.statistics?.receipts?.amount_ttc) || 0
        })

        const difference = computed(() => {
            return form.value.end_balance - expectedBalance.value
        })

        const differenceClass = computed(() => {
            if (difference.value === 0) return 'neutral'
            return difference.value > 0 ? 'positive' : 'negative'
        })

        const differenceNote = computed(() => {
            if (difference.value === 0) return 'Montant exact'
            if (difference.value > 0) return 'Excédent'
            return 'Manquant'
        })

        const closeModal = () => {
            form.value = { end_balance: 0 }
            validationErrors.value = {}
            emit('close')
        }

        const submitForm = async () => {
            if (loading.value) return
            
            loading.value = true
            validationErrors.value = {}

            try {
                const payload = {
                    cash_register_id: props.cashRegister.id,
                    end_balance: form.value.end_balance
                }

                await CashRegisterService.closeCashRegister(payload)

                toastStore.addToast({
                    type: 'success',
                    message: 'Caisse fermée avec succès',
                    duration: 4000
                })

                CashRegisterService.removeOpenedCashRegister()

                emit('success')
                closeModal()
            } catch (error) {
                if (error.errors) {
                    validationErrors.value = error.errors
                }
                toastStore.addToast({
                    type: 'error',
                    message: error.message || 'Erreur lors de la fermeture de la caisse',
                    duration: 4000
                })
            } finally {
                loading.value = false
            }
        }

        const formatCurrency = (amount) => {
            return new Intl.NumberFormat('fr-FR', {
                style: 'currency',
                currency: 'XOF',
                minimumFractionDigits: 0
            }).format(amount || 0)
        }

        return {
            loading,
            form,
            validationErrors,
            expectedBalance,
            difference,
            differenceClass,
            differenceNote,
            closeModal,
            submitForm,
            formatCurrency
        }
    }
})
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
}

.modal-container {
    background: white;
    border-radius: 12px;
    width: 100%;
    max-width: 70%;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid #e5e7eb;
}

.modal-header h3 {
    font-size: 18px;
    font-weight: 700;
    color: #111827;
    margin: 0;
}

.modal-close {
    background: none;
    border: none;
    color: #6b7280;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: all 0.2s;
}

.modal-close:hover {
    background-color: #f3f4f6;
    color: #111827;
}

.modal-body {
    padding: 24px;
}

.summary-section,
.details-section,
.expected-balance-section {
    margin-bottom: 24px;
}

.summary-section h4,
.details-section h4 {
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    margin: 0 0 12px 0;
}

.summary-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
}

.summary-item {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 4px;

    &.danger {
        background: #fee2e2;
        border: 1px solid #fca5a5;
    }

    &.success {
        background: #f0fdf4;
        border: 1px solid #86efac;
    }
}

.summary-label {
    font-size: 12px;
    color: #6b7280;
}

.summary-value {
    font-size: 16px;
    font-weight: 700;
    color: #111827;
}

.summary-value.highlight {
    color: #0A5C6B;
}

.details-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
}

.detail-card {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 12px;
    display: flex;
    gap: 12px;
    align-items: center;
}

.detail-icon {
    font-size: 24px;
}

.detail-content {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.detail-label {
    font-size: 11px;
    color: #6b7280;
}

.detail-value {
    font-size: 14px;
    font-weight: 700;
    color: #111827;
}

.detail-count {
    font-size: 11px;
    color: #6b7280;
}

.expected-balance-card {
    background: linear-gradient(135deg, #0A5C6B 0%, #094854 100%);
    color: white;
    border-radius: 8px;
    padding: 16px;
    text-align: center;
}

.expected-label {
    display: block;
    font-size: 12px;
    opacity: 0.9;
    margin-bottom: 8px;
}

.expected-value {
    display: block;
    font-size: 24px;
    font-weight: 700;
    margin-bottom: 4px;
}

.expected-note {
    display: block;
    font-size: 11px;
    opacity: 0.8;
}

.form-group {
    margin-bottom: 20px;
}

.form-group label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 8px;
}

.input-group {
    position: relative;
    display: flex;
    align-items: center;
}

.input-prefix {
    position: absolute;
    left: 12px;
    font-size: 14px;
    color: #6b7280;
    font-weight: 500;
}

.form-control {
    width: 100%;
    padding: 12px 12px 12px 60px;
    border: 1.5px solid #e5e7eb;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    transition: all 0.2s;
}

.form-control:focus {
    outline: none;
    border-color: #0A5C6B;
    background-color: #f9fafb;
}

.form-control.input-error {
    border-color: #ef4444;
}

.field-error {
    display: block;
    margin-top: 6px;
    font-size: 12px;
    color: #ef4444;
}

.difference-section {
    margin-bottom: 20px;
}

.difference-card {
    border-radius: 8px;
    padding: 16px;
    text-align: center;
}

.difference-card.neutral {
    background: #f0fdf4;
    border: 1px solid #86efac;
}

.difference-card.positive {
    background: #fef3c7;
    border: 1px solid #fcd34d;
}

.difference-card.negative {
    background: #fee2e2;
    border: 1px solid #fca5a5;
}

.difference-label {
    display: block;
    font-size: 12px;
    color: #374151;
    margin-bottom: 4px;
}

.difference-value {
    display: block;
    font-size: 20px;
    font-weight: 700;
    color: #111827;
    margin-bottom: 2px;
}

.difference-note {
    display: block;
    font-size: 11px;
    color: #6b7280;
}

.modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px solid #e5e7eb;
}

.btn-cancel,
.btn-primary {
    padding: 10px 20px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-cancel {
    background: white;
    color: #6b7280;
    border: 1.5px solid #e5e7eb;
}

.btn-cancel:hover {
    background: #f9fafb;
    border-color: #d1d5db;
}

.btn-primary {
    background: #0A5C6B;
    color: white;
    border: none;
    display: flex;
    align-items: center;
    gap: 8px;
}

.btn-primary:hover:not(:disabled) {
    background: #094854;
}

.btn-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.spinner-small {
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

@media (max-width: 640px) {
    .summary-grid,
    .details-grid {
        grid-template-columns: 1fr;
    }

    .expected-value {
        font-size: 20px;
    }

    .modal-footer {
        flex-direction: column;
    }

    .btn-cancel,
    .btn-primary {
        width: 100%;
        justify-content: center;
    }
}
</style>

